import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useLocation } from "react-router-dom";
import { Check, FileText, Loader2, Upload, ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTieneRol, useTienePermiso, PERMISO_SUBIR_CERTIFICADO_FIRMADO } from "@/lib/sesion";
import { SeleccionarTodosCheckbox } from "@/components/SeleccionarTodosCheckbox";
import { api } from "@/lib/api";
import { descargarRespuestaBlob } from "@/lib/download";
import { Alert, Button, Card, DataField, PageContainer, PageHeader } from "@/components/ui";

interface AcreditacionRef { id: string; tipo: string; identificador: string; fechaEmision: string | null; }
interface EspecificacionDetalle {
  id: string;
  normaEspTecId: string | null;
  codigo: string | null;
  nombre: string | null;
  tipoUi: string | null;
  consec: number;
  valor: string | null;
  /** El valor traducido al texto del certificado (selectores de NOM-221 P2). */
  valorTexto: string | null;
  saltoFrec: string | null;
  modulacionDigital: string | null;
  hibrido: string | null;
  bandaFrecuencia: string | null;
  sar: string | null;
}
interface NormaDetalle {
  id: string;
  normaCodigo: string;
  normaTitulo: string;
  laboratorioRazonSocial: string | null;
  numReporte: string | null;
  fechaReporte: string | null;
  modelosEvaluados: string | null;
  receptorFm: boolean | null;
  acreditaciones: AcreditacionRef[];
  especificaciones: EspecificacionDetalle[];
}
interface FraccionDetalle { id: string; certificadoText: string | null; fraccionArancelaria: string; nico: string | null; consec: number; }
interface CertificadoDetalle {
  id: string;
  numCertificado: string;
  fechaEmision: string;
  estatus: string;
  modalidad: string | null;
  pdfDisponible: boolean;
  firmadoDisponible: boolean;
  firmadoSubidoEn: string | null;
  firmadoSubidoPorNombre: string | null;
  firmadoNombreOriginal: string | null;
  firmadoSizeBytes: number | null;
  /** Carta de Aceptación (RF-05): segundo documento de cierre, independiente del firmado. */
  cartaAceptacionDisponible: boolean;
  cartaAceptacionSubidoEn: string | null;
  cartaAceptacionNombreOriginal: string | null;
  cartaAceptacionSizeBytes: number | null;
  solicitudId: string;
  solicitudFolio: string;
  esquemaCodigo: string | null;
  tipoProductoCodigo: string | null;
  titularRazonSocial: string;
  titularRfc: string;
  titularDomicilio: string;
  tramitadorRazonSocial: string | null;
  productoNombre: string;
  productoModelo: string;
  marca: string;
  productoFabricante: string | null;
  productoModelos: string | null;
  productoCantidadLote: number | null;
  productoNumImeis: number | null;
  certificadoOrigenId: string | null;
  certificadoOrigenNumero: string | null;
  normas: NormaDetalle[];
  fracciones: FraccionDetalle[];
}

const ROLES_INTERNOS_PDF = [
  "ADMIN_SICEN", "RESPONSABLE_TELECOM", "COORDINADOR_TECNICO",
  "EVALUADOR_TECNICO", "AUDITOR_INTERNO", "GERENTE_CALIDAD", "AUXILIAR_CALIDAD",
];

/**
 * Detalle del Certificado de Conformidad Único. Sirve a la vez como pantalla
 * de Success (cuando se llega por `/telecom/certificados/{id}/success`) y como
 * consulta posterior (`/telecom/certificados/{id}`).
 */
export function CertificadoTelecomDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const esSuccess = location.pathname.endsWith("/success");

  const q = useQuery<CertificadoDetalle>({
    queryKey: ["certificado-telecom", id],
    queryFn: async () => (await api.get<CertificadoDetalle>(`/api/sector/telecom/certificados/${id}`)).data,
    enabled: !!id,
  });

  if (q.isLoading) {
    return <div className="flex items-center justify-center py-20 text-fg-subtle"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Cargando certificado…</div>;
  }
  if (q.error || !q.data) {
    return <Alert variant="warning">No se pudo cargar el certificado.</Alert>;
  }
  const c = q.data;

  return (
    <PageContainer width="wide">
      {esSuccess ? (
        <section className="rounded-xl border border-estado-ok/25 bg-estado-ok-bg p-5 shadow-card">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-estado-ok text-white">
              <Check className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-estado-ok">Certificado emitido correctamente</h1>
              <p className="mt-1 text-sm text-estado-ok">
                El certificado ha sido elaborado. Puedes descargar el PDF oficial cuando esté disponible.
              </p>
            </div>
          </div>
        </section>
      ) : (
        <PageHeader
          backTo="/telecom/certificados/por-certificar"
          backLabel="Volver a Por certificar"
          icon={<FileText className="h-5 w-5" />}
          title="Certificado de Conformidad"
          subtitle={<span className="font-mono">{c.numCertificado}</span>}
        />
      )}

      <Section title="Información del certificado">
        <div className="grid gap-4 sm:grid-cols-3">
          <Read label="No. de certificado" value={c.numCertificado} mono />
          <Read label="Fecha de emisión" value={new Date(c.fechaEmision).toLocaleDateString("es-MX", { dateStyle: "long" } as Intl.DateTimeFormatOptions)} />
          <Read label="Estatus" value={c.estatus} />
          <Read label="Solicitud (folio)" value={c.solicitudFolio} mono />
          {c.modalidad && <Read label="Modalidad" value={c.modalidad === "CON_RELACION" ? "Con relación" : "Sin relación"} />}
        </div>
        {c.certificadoOrigenNumero && (
          <Alert variant="info" className="mt-3">
            Reexpedición por pruebas complementarias del certificado{" "}
            <span className="font-mono font-semibold">{c.certificadoOrigenNumero}</span>
            {c.certificadoOrigenId && (
              <>
                {" · "}
                <Link to={`/telecom/certificados/${c.certificadoOrigenId}`} className="underline transition hover:text-primary-strong">
                  ver certificado original
                </Link>
              </>
            )}
            . El alcance incluye las normas del original más las NOM/DT adicionales.
          </Alert>
        )}
      </Section>

      <Section title="Datos del solicitante">
        <div className="grid gap-3 sm:grid-cols-2">
          <Read label="Razón social" value={c.titularRazonSocial} />
          <Read label="RFC" value={c.titularRfc} mono />
          <Read label="Domicilio fiscal" value={c.titularDomicilio} span={2} />
          {c.tramitadorRazonSocial && <Read label="Tramitador" value={c.tramitadorRazonSocial} span={2} />}
        </div>
      </Section>

      <Section title="Datos del producto">
        <div className="grid gap-3 sm:grid-cols-2">
          <Read label="Producto" value={`${c.productoNombre} ${c.productoModelo}`} />
          <Read label="Marca" value={c.marca} />
          {c.productoFabricante && <Read label="Fabricante" value={c.productoFabricante} />}
          {c.productoModelos && <Read label="Modelos adicionales" value={c.productoModelos} />}
          {c.productoCantidadLote != null && <Read label="Productos por lote" value={c.productoCantidadLote.toLocaleString("es-MX")} />}
          {c.productoNumImeis != null && <Read label="Núm. de IMEIs" value={c.productoNumImeis.toLocaleString("es-MX")} />}
        </div>
      </Section>

      {c.normas.map((n) => <NormaCard key={n.id} norma={n} />)}

      {c.fracciones.length > 0 && (
        <Section title={`Fracciones arancelarias (${c.fracciones.length})`}>
          <div className="w-full overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/80 text-xs uppercase text-fg-subtle">
              <tr>
                <th className="px-3 py-2 text-left">Certificado</th>
                <th className="px-3 py-2 text-left">Fracción arancelaria</th>
                <th className="px-3 py-2 text-left">NICO</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-edge">
              {c.fracciones.map((f) => (
                <tr key={f.id}>
                  <td className="px-3 py-2 text-xs">{f.certificadoText ?? "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono">{f.fraccionArancelaria}</td>
                  <td className="px-3 py-2 text-xs font-mono">{f.nico ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Section>
      )}

      <PdfSection certificado={c} />


      {esSuccess && (
        <div className="flex justify-end">
          <Link to="/telecom/certificados/por-certificar">
            <Button variant="secondary" icon={<Check className="h-4 w-4" />}>
              Aceptar (volver a Por certificar)
            </Button>
          </Link>
        </div>
      )}
    </PageContainer>
  );
}

/**
 * Sección PDF oficial — comportamiento por rol:
 * - Internos (con permiso CERTIFICADO.SUBIR_FIRMADO):
 *     · Botón "Descargar borrador" (Jasper)
 *     · Si pueden subir firmado: input file para subirlo / reemplazarlo
 *     · Botón "Descargar firmado" si ya existe
 * - Solicitante (titular/tramitador/RL/gestor): solo el firmado cuando exista,
 *     mientras tanto un aviso "El certificado está en proceso de firma".
 */
function PdfSection({ certificado: c }: { certificado: CertificadoDetalle }) {
  // Identidad efectiva, no la del token: bajo suplantación ofrecía subir el PDF
  // firmado a quien no puede hacerlo.
  const esInterno = useTieneRol(...ROLES_INTERNOS_PDF);
  // Capacidad, no rol: la lista de quién puede vive en kernel.rol_permiso.
  const puedeSubirFirmado = useTienePermiso(PERMISO_SUBIR_CERTIFICADO_FIRMADO);

  return (
    <Section title="PDF oficial">
      <div className="space-y-4">
        {/* PDF firmado: visible para todos los autorizados si existe */}
        {c.firmadoDisponible ? (
          <PdfFirmadoBox certificado={c} />
        ) : (
          !esInterno && (
            <Alert variant="warning">
              El certificado está en proceso de firma. Una vez que el equipo de NPMX
              lo firme y lo cargue, podrás descargarlo desde aquí.
            </Alert>
          )
        )}

        {/* Borrador Jasper: solo internos */}
        {esInterno && (
          <DescargarPdfBorradorButton certificadoId={c.id} numCertificado={c.numCertificado} />
        )}

        {/* Uploader: solo quienes pueden subir el firmado */}
        {puedeSubirFirmado && (
          <SubirPdfFirmadoControl certificadoId={c.id} firmadoYaSubido={c.firmadoDisponible} />
        )}

        {/* Carta de Aceptación (RF-05): documento de cierre independiente */}
        {c.cartaAceptacionDisponible ? (
          <CartaAceptacionBox certificado={c} />
        ) : (
          !esInterno && (
            <Alert variant="warning">
              La Carta de Aceptación aún no está disponible.
            </Alert>
          )
        )}
        {puedeSubirFirmado && (
          <SubirCartaAceptacionControl certificadoId={c.id} cartaYaSubida={c.cartaAceptacionDisponible} />
        )}
        {esInterno && !(c.firmadoDisponible && c.cartaAceptacionDisponible) && (
          <Alert variant="warning">
            Para concluir esta etapa se requieren los dos documentos (certificado firmado y Carta de
            Aceptación). La notificación de disponibilidad al cliente se habilita cuando ambos estén cargados.
          </Alert>
        )}
      </div>
    </Section>
  );
}

/** Caja informativa de la Carta de Aceptación (RF-05) + botón de descarga. */
function CartaAceptacionBox({ certificado: c }: { certificado: CertificadoDetalle }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const descargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get(`/api/sector/telecom/certificados/${c.id}/carta-aceptacion`, {
        responseType: "blob",
      });
      descargarRespuestaBlob(resp, c.cartaAceptacionNombreOriginal ?? `Carta-Aceptacion-${c.numCertificado}.pdf`);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? "Error al descargar la Carta de Aceptación.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded border border-estado-ok/25 bg-estado-ok-bg p-3">
      <div className="mb-2 flex items-start gap-2 text-sm text-estado-ok">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <div className="font-medium">Carta de Aceptación disponible</div>
          <div className="text-xs text-estado-ok">
            {c.cartaAceptacionNombreOriginal && <>{c.cartaAceptacionNombreOriginal}. </>}
            {c.cartaAceptacionSubidoEn && <>{new Date(c.cartaAceptacionSubidoEn).toLocaleString("es-MX")}.</>}
            {c.cartaAceptacionSizeBytes != null && <> {Math.round(c.cartaAceptacionSizeBytes / 1024)} KB.</>}
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <Button variant="success" loading={loading} icon={<FileText className="h-4 w-4" />} onClick={descargar}>
          {loading ? "Descargando…" : "Descargar Carta de Aceptación"}
        </Button>
        {error && <Alert variant="error">{error}</Alert>}
      </div>
    </div>
  );
}

/** Uploader de la Carta de Aceptación (RF-05). Sin notificación: esa va con el firmado. */
function SubirCartaAceptacionControl({ certificadoId, cartaYaSubida }: { certificadoId: string; cartaYaSubida: boolean }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMessage, setOkMessage] = useState<string | null>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      setError("El archivo debe ser un PDF."); return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError("El PDF excede el tamaño máximo permitido (20 MB)."); return;
    }
    setError(null);
    setOkMessage(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/api/sector/telecom/certificados/${certificadoId}/carta-aceptacion`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOkMessage(cartaYaSubida
        ? "Carta de Aceptación reemplazada correctamente."
        : "Carta de Aceptación subida correctamente.");
      await qc.invalidateQueries({ queryKey: ["certificado-telecom", certificadoId] });
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? err?.message ?? "Error al subir la Carta de Aceptación.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded border border-dashed border-edge-strong bg-surface-2 p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
        {cartaYaSubida ? "Reemplazar Carta de Aceptación" : "Subir Carta de Aceptación"}
      </div>
      <input ref={inputRef} type="file" accept="application/pdf,.pdf" hidden onChange={handleFile} />
      <Button
        variant="secondary"
        size="sm"
        disabled={uploading}
        loading={uploading}
        icon={<Upload className="h-4 w-4" />}
        onClick={() => inputRef.current?.click()}
      >
        {cartaYaSubida ? "Seleccionar otro PDF" : "Seleccionar PDF de la carta"}
      </Button>
      <div className="mt-1 text-[11px] text-fg-subtle">Solo PDF. Tamaño máximo 20 MB.</div>
      {okMessage && <Alert variant="success" className="mt-2">{okMessage}</Alert>}
      {error && <Alert variant="error" className="mt-2">{error}</Alert>}
    </div>
  );
}

/** Caja informativa del PDF firmado + botón de descarga. */
function PdfFirmadoBox({ certificado: c }: { certificado: CertificadoDetalle }) {
  return (
    <div className="rounded border border-estado-ok/25 bg-estado-ok-bg p-3">
      <div className="mb-2 flex items-start gap-2 text-sm text-estado-ok">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <div className="font-medium">Certificado firmado disponible</div>
          <div className="text-xs text-estado-ok">
            {c.firmadoSubidoPorNombre && <>Subido por {c.firmadoSubidoPorNombre}. </>}
            {c.firmadoSubidoEn && <>{new Date(c.firmadoSubidoEn).toLocaleString("es-MX")}.</>}
            {c.firmadoSizeBytes != null && <> {Math.round(c.firmadoSizeBytes / 1024)} KB.</>}
          </div>
        </div>
      </div>
      <DescargarPdfFirmadoButton certificadoId={c.id} numCertificado={c.numCertificado} />
    </div>
  );
}

/**
 * Descarga el PDF borrador (Jasper) vía fetch autenticado y lo abre en una
 * nueva pestaña. Solo internos llaman este componente.
 */
function DescargarPdfBorradorButton({ certificadoId, numCertificado }: { certificadoId: string; numCertificado: string }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get(`/api/sector/telecom/certificados/${certificadoId}/pdf`, {
        responseType: "blob",
      });
      descargarRespuestaBlob(resp, `Certificado-${numCertificado}-borrador.pdf`);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? "Error al generar el PDF.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Button
        variant="secondary"
        loading={loading}
        icon={<FileText className="h-4 w-4" />}
        onClick={handleClick}
      >
        {loading ? "Generando…" : "Descargar borrador (no firmado)"}
      </Button>
      {error && <Alert variant="error">{error}</Alert>}
    </div>
  );
}

/** Botón que descarga el PDF firmado (MinIO). */
function DescargarPdfFirmadoButton({ certificadoId, numCertificado }: { certificadoId: string; numCertificado: string }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get(`/api/sector/telecom/certificados/${certificadoId}/firmado`, {
        responseType: "blob",
      });
      descargarRespuestaBlob(resp, `Certificado-${numCertificado}-firmado.pdf`);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? "Error al descargar el PDF firmado.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Button
        variant="success"
        loading={loading}
        icon={<FileText className="h-4 w-4" />}
        onClick={handleClick}
      >
        {loading ? "Descargando…" : "Descargar PDF firmado"}
      </Button>
      {error && <Alert variant="error">{error}</Alert>}
    </div>
  );
}

/**
 * Control de subida del PDF firmado. Visible sólo a quienes tienen el permiso
 * CERTIFICADO.SUBIR_FIRMADO. Si ya hay un firmado
 * subido, el botón pasa a "Reemplazar PDF firmado" (mantiene historia en
 * bitácora).
 */
interface DestinatarioCand {
  id: string;
  correo: string;
  nombre: string;
  tipo?: string;
  rlRazonSocial?: string;
  /** «Representante legal» o «Usuario autorizado». */
  relacion?: string;
}

function SubirPdfFirmadoControl({ certificadoId, firmadoYaSubido }: { certificadoId: string; firmadoYaSubido: boolean }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMessage, setOkMessage] = useState<string | null>(null);

  // Carga los candidatos a notificar solo cuando hay un archivo seleccionado.
  const qDest = useQuery({
    queryKey: ["certificado-destinatarios", certificadoId],
    queryFn: async () =>
      (await api.get<{ internos: DestinatarioCand[]; relacionados: DestinatarioCand[]; preseleccionados?: string[] }>(
        `/api/sector/telecom/certificados/${certificadoId}/destinatarios-notificacion`,
      )).data,
    enabled: !!pendingFile,
  });

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      setError("El archivo debe ser un PDF."); return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError("El PDF excede el tamaño máximo permitido (20 MB)."); return;
    }
    setError(null);
    setOkMessage(null);
    setPendingFile(file);
  };

  const cancelar = () => {
    setPendingFile(null);
    setError(null);
  };

  const subir = async (destinatariosIds: string[]) => {
    if (!pendingFile) return;
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", pendingFile);
      destinatariosIds.forEach((id) => form.append("destinatariosUserIds", id));
      await api.post(`/api/sector/telecom/certificados/${certificadoId}/firmado`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const msgBase = firmadoYaSubido
        ? "PDF firmado reemplazado correctamente."
        : "PDF firmado subido correctamente.";
      setOkMessage(destinatariosIds.length === 0
        ? `${msgBase} No se enviaron correos.`
        : `${msgBase} Se notificó a ${destinatariosIds.length} destinatario${destinatariosIds.length === 1 ? "" : "s"}.`);
      setPendingFile(null);
      await qc.invalidateQueries({ queryKey: ["certificado-telecom", certificadoId] });
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? err?.message ?? "Error al subir el PDF firmado.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded border border-dashed border-edge-strong bg-surface-2 p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
        {firmadoYaSubido ? "Reemplazar PDF firmado" : "Subir PDF firmado oficial"}
      </div>
      <input ref={inputRef} type="file" accept="application/pdf,.pdf" hidden onChange={handleFile} />

      {!pendingFile && (
        <>
          <Button
            variant="secondary"
            size="sm"
            disabled={uploading}
            icon={<Upload className="h-4 w-4" />}
            onClick={() => inputRef.current?.click()}
          >
            {firmadoYaSubido ? "Seleccionar otro PDF" : "Seleccionar PDF firmado"}
          </Button>
          <div className="mt-1 text-[11px] text-fg-subtle">Solo PDF. Tamaño máximo 20 MB.</div>
        </>
      )}

      {pendingFile && (
        <DestinatariosPicker
          file={pendingFile}
          destinatarios={qDest.data}
          cargando={qDest.isLoading}
          uploading={uploading}
          onCancelar={cancelar}
          onSubir={subir}
        />
      )}

      {okMessage && <Alert variant="success" className="mt-2">{okMessage}</Alert>}
      {error && <Alert variant="error" className="mt-2">{error}</Alert>}
    </div>
  );
}

/**
 * Sub-componente: una vez que el interno eligió el PDF a subir, muestra la
 * lista de candidatos a notificar (internos + relacionados al titular y
 * tramitador) con checkboxes. Todos pre-seleccionados. El interno puede
 * destacharlos o subir sin notificar.
 */
function DestinatariosPicker({
  file, destinatarios, cargando, uploading, onCancelar, onSubir,
}: {
  file: File;
  destinatarios?: { internos: DestinatarioCand[]; relacionados: DestinatarioCand[]; preseleccionados?: string[] };
  cargando: boolean;
  uploading: boolean;
  onCancelar: () => void;
  onSubir: (destinatariosIds: string[]) => void;
}) {
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [tocado, setTocado] = useState(false);

  // RF-02 (mismo comportamiento que el modal de avance): sembrar la selección
  // con los destinatarios recordados de la empresa durante el flujo; sin
  // memoria, se marcan todos los candidatos (comportamiento previo). Solo
  // mientras el usuario no haya tocado la lista, para no pisar sus cambios.
  useEffect(() => {
    if (!destinatarios || tocado) return;
    const pre = destinatarios.preseleccionados;
    const ids = pre && pre.length > 0
      ? pre
      : [...destinatarios.internos, ...destinatarios.relacionados].map((d) => d.id);
    setMarcados(new Set(ids));
  }, [destinatarios, tocado]);

  const toggle = (id: string) => {
    setTocado(true);
    setMarcados((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  // Una gestoría puede tener quince personas autorizadas; marcarlas de una en
  // una era el trabajo que este control ahorra.
  const toggleTodos = (ids: string[], marcar: boolean) => {
    setTocado(true);
    setMarcados((prev) => {
      const s = new Set(prev);
      ids.forEach((id) => (marcar ? s.add(id) : s.delete(id)));
      return s;
    });
  };

  return (
    <div className="space-y-3">
      <div className="rounded border border-edge bg-surface p-2 text-xs">
        <span className="font-medium">Archivo:</span> {file.name}
        <span className="ml-2 text-fg-subtle">({Math.round(file.size / 1024)} KB)</span>
      </div>

      {cargando && (
        <div className="flex items-center gap-2 text-sm text-fg-subtle">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando destinatarios…
        </div>
      )}

      {destinatarios && (
        <div className="space-y-3 text-sm">
          <DestinatariosGroup
            title="Solicitante y empresa tramitadora"
            destinatarios={destinatarios.relacionados}
            marcados={marcados}
            onToggle={toggle}
            onToggleTodos={toggleTodos}
            empty="No hay usuarios vigentes en el titular ni en su empresa tramitadora."
          />
          <DestinatariosGroup
            title="Internos NPMX"
            destinatarios={destinatarios.internos}
            marcados={marcados}
            onToggle={toggle}
            onToggleTodos={toggleTodos}
            empty="No hay usuarios internos disponibles."
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          loading={uploading}
          disabled={cargando}
          icon={<Upload className="h-4 w-4" />}
          onClick={() => onSubir([...marcados])}
        >
          {uploading ? "Subiendo…" : `Subir y notificar a ${marcados.size}`}
        </Button>
        <Button
          variant="secondary"
          disabled={uploading || cargando}
          onClick={() => onSubir([])}
        >
          Subir sin notificar
        </Button>
        <Button variant="ghost" disabled={uploading} onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

function DestinatariosGroup({
  title, destinatarios, marcados, onToggle, onToggleTodos, empty,
}: {
  title: string;
  destinatarios: DestinatarioCand[];
  marcados: Set<string>;
  onToggle: (id: string) => void;
  onToggleTodos: (ids: string[], marcar: boolean) => void;
  empty: string;
}) {
  const ids = destinatarios.map((d) => d.id);

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-fg-subtle">{title}</div>
        <SeleccionarTodosCheckbox ids={ids} seleccionados={marcados} onToggleTodos={onToggleTodos} />
      </div>
      {destinatarios.length === 0 ? (
        <div className="text-xs italic text-fg-faint">{empty}</div>
      ) : (
        <ul className="space-y-1">
          {destinatarios.map((d) => (
            <li key={d.id}>
              <label className="flex cursor-pointer items-start gap-2 rounded border border-edge bg-surface px-2 py-1 transition hover:bg-primary-tint">
                <input
                  type="checkbox"
                  checked={marcados.has(d.id)}
                  onChange={() => onToggle(d.id)}
                  className="mt-0.5 h-4 w-4"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-fg">{d.nombre || d.correo}</div>
                  <div className="truncate text-xs text-fg-subtle">
                    {d.correo}
                    {d.rlRazonSocial && <> · {d.rlRazonSocial}</>}
                    {d.relacion && <> · {d.relacion}</>}
                  </div>
                </div>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NormaCard({ norma }: { norma: NormaDetalle }) {
  const accsPorTipo = new Map<string, AcreditacionRef[]>();
  norma.acreditaciones.forEach((a) => {
    if (!accsPorTipo.has(a.tipo)) accsPorTipo.set(a.tipo, []);
    accsPorTipo.get(a.tipo)!.push(a);
  });

  return (
    <Section title={norma.normaCodigo}>
      <div className="text-xs text-fg-muted">{norma.normaTitulo}</div>
      {norma.normaCodigo === "NOM-221-SCFI-2017-P1" ? (
        <Read label="Receptor FM" value={norma.receptorFm == null ? "—" : norma.receptorFm ? "Sí cuenta con receptor FM" : "No cuenta con receptor FM"} />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 mt-2">
            <Read label="Laboratorio" value={norma.laboratorioRazonSocial ?? "—"} span={2} />
            <Read label="No. de reporte" value={norma.numReporte ?? "—"} mono />
            <Read label="Fecha de reporte" value={norma.fechaReporte ?? "—"} />
            {norma.modelosEvaluados && <Read label="Modelos evaluados" value={norma.modelosEvaluados} span={2} />}
          </div>
          {accsPorTipo.size > 0 && (
            <div className="mt-3 space-y-2">
              {Array.from(accsPorTipo.entries()).map(([tipo, items]) => (
                <div key={tipo}>
                  <div className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                    {tipo === "EMA" ? "Acreditación EMA" : tipo === "IFT" ? "Autorización IFT" : tipo === "DGN" ? "Aprobación DGN" : "Aprobación CONUEE"}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {items.map((a) => (
                      <span key={a.id} className="rounded bg-surface-2 px-2 py-0.5 text-xs font-mono">{a.identificador}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {norma.especificaciones.length > 0 && (
            <div className="mt-3">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-subtle">Especificaciones técnicas</div>
              <EspecificacionesView ets={norma.especificaciones} />
            </div>
          )}
        </>
      )}
    </Section>
  );
}

function EspecificacionesView({ ets }: { ets: EspecificacionDetalle[] }) {
  if (ets.length === 0) return <div className="text-xs text-fg-subtle">Sin especificaciones registradas.</div>;
  const tipo = ets[0].tipoUi;

  if (tipo === "TEXTAREA_3COLS") {
    return (
      <div className="w-full overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-surface-2/80 text-xs uppercase text-fg-subtle">
          <tr><th className="px-2 py-1 text-left">ET</th><th className="px-2 py-1">Salto</th><th className="px-2 py-1">Modulación</th><th className="px-2 py-1">Híbrido</th></tr>
        </thead>
        <tbody>
          {ets.map((e) => (
            <tr key={e.id} className="border-t">
              <td className="px-2 py-1 text-xs">{e.nombre}</td>
              <td className="px-2 py-1 text-xs">{e.saltoFrec ?? "—"}</td>
              <td className="px-2 py-1 text-xs">{e.modulacionDigital ?? "—"}</td>
              <td className="px-2 py-1 text-xs">{e.hibrido ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    );
  }
  if (tipo === "DINAMICA_BANDA_SAR") {
    return (
      <div className="w-full overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-surface-2/80 text-xs uppercase text-fg-subtle">
          <tr><th className="px-2 py-1 text-left">Banda de frecuencia</th><th className="px-2 py-1 text-left">SAR</th></tr>
        </thead>
        <tbody>
          {ets.map((e) => (
            <tr key={e.id} className="border-t"><td className="px-2 py-1 text-xs">{e.bandaFrecuencia ?? "—"}</td><td className="px-2 py-1 text-xs">{e.sar ?? "—"}</td></tr>
          ))}
        </tbody>
      </table>
      </div>
    );
  }
  return (
    <div className="w-full overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-surface-2/80 text-xs uppercase text-fg-subtle"><tr><th className="px-2 py-1 text-left">ET</th><th className="px-2 py-1 text-left">Valor</th></tr></thead>
      <tbody>
        {ets.map((e) => (
          <tr key={e.id} className="border-t"><td className="px-2 py-1 text-xs">{e.nombre ?? "(ad-hoc)"}</td><td className="px-2 py-1 text-xs">{e.valorTexto ?? e.valor ?? "—"}</td></tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

/** Sección estándar del detalle: ahora sobre el primitivo `Card`. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card title={title} bodyClassName="space-y-3">
      {children}
    </Card>
  );
}

/** Par etiqueta/valor de solo lectura sobre `DataField` (soporta mono y span). */
function Read({ label, value, mono = false, span = 1 }: { label: string; value: string; mono?: boolean; span?: 1 | 2 }) {
  return (
    <DataField
      className={span === 2 ? "sm:col-span-2" : undefined}
      label={label}
      value={mono ? <span className="font-mono text-xs">{value}</span> : value}
    />
  );
}
